import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];
  /** @type {{ image: HTMLImageElement, src: string | null, srcset: string | null } | null} */
  #hoverPreviewState = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #hoverIntentTimer = null;
  /** True after a delayed hover preview has actually advanced the slideshow. */
  #hoverPreviewArmed = false;
  /** Bumped to cancel an in-flight decode→commit after pointerleave. */
  #hoverPreviewGeneration = 0;
  /**
   * Delay before swapping the card image on hover. Rapid pointerenter/leave while
   * sweeping across a product grid previously paid ~28ms slideshow.next + ~28ms
   * slideshow.previous per card the user never lingered on. Waiting briefly skips
   * those no-op swaps; intentional hovers still preview after the delay.
   */
  static HOVER_INTENT_MS = 70;
  #linkedProductPreviewLogged = new Set();
  static linkedProductPreviewCache = new Map();
  static HOVER_PRELOAD_CARD_LIMIT = 6;

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the hover image as soon as the card is connected.
    requestAnimationFrame(() => {
      if (this.#isWithinHoverPreloadLimit()) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  #isWithinHoverPreloadLimit() {
    const cardItem = this.closest('li.product-grid__item');
    if (!cardItem) return false;

    const indexClass = cardItem.className
      .split(' ')
      .find((className) => className.startsWith('product-grid__item--'));
    if (!indexClass) return false;

    const index = Number.parseInt(indexClass.replace('product-grid__item--', ''), 10);
    return Number.isFinite(index) && index >= 0 && index < ProductCard.HOVER_PRELOAD_CARD_LIMIT;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    if (this.#hoverIntentTimer) {
      clearTimeout(this.#hoverIntentTimer);
      this.#hoverIntentTimer = null;
    }
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    const nextSlide = currentSlide?.nextElementSibling;

    this.#preloadSlideImage(nextSlide);
  }

  /**
   * @param {Element | null | undefined} slide
   */
  #preloadSlideImage(slide) {
    const image = slide?.querySelector('img');
    if (!(image instanceof HTMLImageElement)) return;

    image.removeAttribute('loading');
    image.fetchPriority = 'high';
    image.decode().catch(() => {});
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();

  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    const selectedOption = this.variantPicker?.selectedOption;
    if (!selectedOption) {
      return;
    }

    const selectedImageId = selectedOption.dataset.optionMediaId;
    const selectedImageKey = selectedImageId ? String(selectedImageId) : '';

    if (slideshow && selectedImageKey) {
      const slides = slideshow.slides ?? slideshow.refs?.slides ?? [];
      const hasMatchingSlide = slides.some(
        (slide) => String(slide.getAttribute('slide-id')) === selectedImageKey
      );

      if (hasMatchingSlide) {
        for (const slide of slides) {
          if (slide.getAttribute('variant-image') == null) continue;

          slide.hidden = String(slide.getAttribute('slide-id')) !== selectedImageKey;
        }

        slideshow.select({ id: selectedImageKey }, undefined, { animate: false });
        return;
      }
    }

    // Connected/grouped-product swatch: the selected color's media is not a slide in
    // this card's slideshow, so fall back to the swatch's preview image and commit it
    // (clearing the hover-restore state so it persists after pointerleave).
    const previewImageUrl = selectedOption.dataset.previewImageUrl;
    if (previewImageUrl) {
      this.#applyHoverPreviewImage(previewImageUrl);
      this.#hoverPreviewState = null;
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string | number | Event} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    const resolvedPreview = this.#resolvePreviewRequest(id);
    const slideIds = slideshow.slides?.map((slide) => slide.getAttribute('slide-id')) ?? [];
    const resolvedLabel =
      id instanceof Event && id.target instanceof Element
        ? /** @type {HTMLLabelElement | null} */ (id.target.closest('label[data-media-id]'))
        : resolvedPreview.mediaId
          ? /** @type {HTMLLabelElement | null} */ (this.querySelector(`label[data-media-id="${resolvedPreview.mediaId}"]`))
          : null;
    const resolvedInput = /** @type {HTMLInputElement | null} */ (
      resolvedLabel?.querySelector('input[data-option-value-id]') ?? null
    );

    this.resetVariant.cancel();

    if (!resolvedPreview.mediaId && !resolvedPreview.previewImageUrl && resolvedInput?.dataset.connectedProductUrl) {
      this.#hydrateLinkedProductPreview(
        resolvedInput.dataset.connectedProductUrl,
        resolvedInput
      );
    }

    if (resolvedPreview.mediaId && slideIds.includes(String(resolvedPreview.mediaId))) {
      this.#restoreHoverPreviewImage();
      slideshow.select({ id: resolvedPreview.mediaId }, undefined, { animate: false });
      return;
    }

    if (resolvedPreview.previewImageUrl) {
      this.#applyHoverPreviewImage(resolvedPreview.previewImageUrl);
    }
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#hoverIntentTimer) {
      clearTimeout(this.#hoverIntentTimer);
      this.#hoverIntentTimer = null;
    }

    this.#hoverIntentTimer = setTimeout(() => {
      this.#hoverIntentTimer = null;
      this.#commitHoverPreview();
    }, ProductCard.HOVER_INTENT_MS);
  }

  /**
   * Actually advance the slideshow for an intentional hover (after hover-intent delay).
   * Uses a generation token so a leave during image.decode() cannot commit after reset.
   */
  #commitHoverPreview() {
    const { slideshow } = this.refs;
    if (!slideshow || this.#hoverPreviewArmed) return;

    const generation = ++this.#hoverPreviewGeneration;

    const currentSlide = slideshow.slides?.[slideshow.current];
    const nextSlide = currentSlide?.nextElementSibling;
    const nextImageBeforeHover = nextSlide?.querySelector('img');

    const apply = () => {
      if (generation !== this.#hoverPreviewGeneration || !this.isConnected) return;

      if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
        slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
      } else {
        slideshow.next(undefined, { animate: false });
        requestAnimationFrame(() => this.#preloadNextPreviewImage());
      }
      this.#hoverPreviewArmed = true;
    };

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      apply();
      return;
    }

    this.#preloadSlideImage(nextSlide);
    if (nextImageBeforeHover instanceof HTMLImageElement && !nextImageBeforeHover.complete) {
      nextImageBeforeHover
        .decode()
        .catch(() => {})
        .finally(apply);
    } else {
      apply();
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    // Cancel pending hover-intent and any in-flight decode→commit.
    if (this.#hoverIntentTimer) {
      clearTimeout(this.#hoverIntentTimer);
      this.#hoverIntentTimer = null;
      this.#hoverPreviewGeneration += 1;
      return;
    }

    // Cancel in-flight decode commit that hasn't armed yet.
    this.#hoverPreviewGeneration += 1;

    if (!this.#hoverPreviewArmed) {
      return;
    }

    this.#hoverPreviewArmed = false;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.#restoreHoverPreviewImage();

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      const hasMatchingSlide = id && slideshow.slides?.some((slide) => slide.getAttribute('slide-id') === id);

      if (hasMatchingSlide) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }

      // Connected/grouped-product selection: no slide to switch to, so re-apply the
      // committed preview image on the active slide so it persists after hover/leave.
      const previewImageUrl = this.variantPicker.selectedOption.dataset.previewImageUrl;
      if (previewImageUrl) {
        this.#applyHoverPreviewImage(previewImageUrl);
        this.#hoverPreviewState = null;
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);

  /**
   * @param {string | number | Event} idOrEvent
   * @returns {{ mediaId: string | null, previewImageUrl: string | null }}
   */
  #resolvePreviewRequest(idOrEvent) {
    /** @type {HTMLLabelElement | null} */
    let label = null;

    if (idOrEvent instanceof Event && idOrEvent.target instanceof Element) {
      label = /** @type {HTMLLabelElement | null} */ (idOrEvent.target.closest('label[data-media-id]'));
    } else if (idOrEvent !== null && idOrEvent !== undefined) {
      const requestedId = String(idOrEvent);
      label =
        /** @type {HTMLLabelElement | null} */ (this.querySelector(`label[data-media-id="${requestedId}"]`)) ??
        /** @type {HTMLLabelElement | null} */ (
          this.querySelector(`input[data-option-media-id="${requestedId}"]`)?.closest('label') ?? null
        );
    }

    const input = /** @type {HTMLInputElement | null} */ (label?.querySelector('input[data-option-value-id]') ?? null);
    const rawMediaId =
      typeof idOrEvent === 'string' || typeof idOrEvent === 'number'
        ? String(idOrEvent)
        : label?.dataset.mediaId || input?.dataset.optionMediaId || '';
    const mediaId = rawMediaId && rawMediaId !== '[object Object]' ? rawMediaId : null;
    const previewImageUrl = label?.dataset.previewImageUrl || input?.dataset.previewImageUrl || null;

    return { mediaId, previewImageUrl };
  }

  /**
   * @param {string} previewImageUrl
   */
  #applyHoverPreviewImage(previewImageUrl) {
    const currentSlideIndex =
      typeof this.refs.slideshow?.current === 'number' ? this.refs.slideshow.current : null;
    const activeSlide =
      (currentSlideIndex != null ? this.refs.slideshow?.slides?.[currentSlideIndex] : null) ??
      this.refs.slideshow?.slides?.find((slide) => slide.getAttribute('aria-hidden') === 'false') ??
      this.refs.slideshow?.slides?.[0];
    const activeImage = activeSlide?.querySelector('img');

    if (!(activeImage instanceof HTMLImageElement)) return;

    if (!this.#hoverPreviewState) {
      this.#hoverPreviewState = {
        image: activeImage,
        src: activeImage.getAttribute('src'),
        srcset: activeImage.getAttribute('srcset'),
      };
    }

    activeImage.src = previewImageUrl;
    activeImage.srcset = previewImageUrl;
  }

  #restoreHoverPreviewImage() {
    if (!this.#hoverPreviewState) return;

    const { image, src, srcset } = this.#hoverPreviewState;

    if (src) {
      image.src = src;
    }

    if (srcset) {
      image.srcset = srcset;
    } else {
      image.removeAttribute('srcset');
    }

    this.#hoverPreviewState = null;
  }

  /**
   * @param {string} connectedProductUrl
   * @param {HTMLInputElement} input
   */
  async #hydrateLinkedProductPreview(connectedProductUrl, input) {
    const label = input.closest('label');
    if (!(label instanceof HTMLLabelElement)) return;

    const cachedPreview = ProductCard.linkedProductPreviewCache.get(connectedProductUrl);
    if (cachedPreview) {
      if (!input.dataset.previewImageUrl && cachedPreview.previewImageUrl) {
        input.dataset.previewImageUrl = cachedPreview.previewImageUrl;
      }
      if (!label.dataset.previewImageUrl && cachedPreview.previewImageUrl) {
        label.dataset.previewImageUrl = cachedPreview.previewImageUrl;
      }
      if (cachedPreview.previewImageUrl && label.matches(':hover')) {
        this.#applyHoverPreviewImage(cachedPreview.previewImageUrl);
      }
      return;
    }

    if (this.#linkedProductPreviewLogged.has(connectedProductUrl)) return;
    this.#linkedProductPreviewLogged.add(connectedProductUrl);

    try {
      const response = await fetch(`${connectedProductUrl}.js`);
      const payload = response.ok ? await response.json() : null;
      const previewImageUrl =
        typeof payload?.featured_image === 'string'
          ? payload.featured_image.replace(/^https?:/, '')
          : Array.isArray(payload?.images) && typeof payload.images[0] === 'string'
            ? payload.images[0].replace(/^https?:/, '')
            : null;

      if (previewImageUrl) {
        input.dataset.previewImageUrl = previewImageUrl;
        label.dataset.previewImageUrl = previewImageUrl;
        ProductCard.linkedProductPreviewCache.set(connectedProductUrl, { previewImageUrl });
        if (label.matches(':hover')) {
          this.#applyHoverPreviewImage(previewImageUrl);
        }
      }
    } catch {
      this.#linkedProductPreviewLogged.delete(connectedProductUrl);
    }
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  #restoreExpandedSwatches() {
    const { overflowList } = this.refs;
    const shouldRestore = this.dataset.swatchesExpanded === 'true';

    if (shouldRestore && overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }

  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
    this.#restoreExpandedSwatches();
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * @param {Element | Document} updatedHtml
   * @returns {{ id: string, url: string } | undefined}
   */
  updateVariantPicker(updatedHtml) {
    const newProduct = super.updateVariantPicker(updatedHtml);
    this.#restoreExpandedSwatches();
    return newProduct;
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;
    this.dataset.swatchesExpanded = 'true';

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
